// src/api/agentTestChatService.js
import apiClient from './axiosConfig'; // For initSession and getConversationHistory

// Shared error handler (can be imported from a utils file if used elsewhere)
const handleApiError = (error, operation) => {
  console.error(`Error ${operation}:`, error);
  let message = error.response?.data?.detail || error.message || `Failed to ${operation}.`;
  // If detail is an object (e.g., Pydantic validation error), try to stringify it or get a message field.
  if (typeof error.response?.data?.detail === 'object' && error.response?.data?.detail !== null) {
    if (error.response.data.detail.message) {
        message = error.response.data.detail.message;
    } else {
        try {
            message = JSON.stringify(error.response.data.detail);
        } catch (e) {
            // Fallback if stringify fails
            message = error.response?.data?.message || `Failed to ${operation} due to complex error object.`;
        }
    }
  } else if (typeof error.response?.data?.detail !== 'string' && error.response?.data?.message) {
    message = error.response.data.message;
  }
  const status = error.response?.status || null;
  if (message === `Failed to ${operation}.` && status) {
    message = `Request failed with status code ${status} while ${operation}.`;
  }
  return { isError: true, message, status, originalError: error };
};

export const agentTestChatService = {
  /**
   * Initializes a secure test chat session.
   * The backend might return 'recent_conversations' like [{ id: 'conv1', label: 'Chat from 10:30 AM' }]
   */
  async initSession(agentId, initialContext = null) {
    if (!agentId) {
      return { isError: true, message: "Agent ID is required.", status: 400 };
    }
    try {
      const payload = initialContext ? { initial_context: initialContext } : {};
      const response = await apiClient.post(`/agents/${agentId}/init-test-chat`, payload);
      // response.data should contain:
      // { wat: "...", wat_expires_utc: ..., api_base_url: "...", llm_model_id: "...", 
      //   system_prompt_text: "...", enabled_tools: [...], agent_name: "...",
      //   recent_conversations: [ { id: "conv_xyz", label: "Optional Label", timestamp: "ISO_DATE_STRING" }, ... ] // Optional
      // }
      return response.data;
    } catch (error) {
      return handleApiError(error, `initialize test chat session for agent ${agentId}`);
    }
  },

  /**
   * Establishes an SSE stream for an initialized test chat session using fetch.
   * @param {string} agentId - The ID of the agent.
   * @param {string} rootApiUrl - The base URL for the agent's API.
   * @param {object} requestBody - The body of the request, e.g., { user_message: "...", conversation_id: "..." }.
   * @param {string} testChatWat - The Web Access Token for the test chat.
   * @param {function} onMessage - Callback for each SSE message received.
   * @param {function} onError - Callback for errors during the SSE connection.
   * @param {function} onOpen - Callback when the SSE connection is successfully opened.
   * @returns {{ close: () => void }} - An object with a method to close/abort the connection.
   */
  streamTestConversation(agentId, rootApiUrl, requestBody, testChatWat, onMessage, onError, onOpen) {
    if (!agentId || !rootApiUrl || !requestBody || !testChatWat || !onMessage || !onError) {
      const errorMsg = "streamTestConversation: Missing required parameters.";
      console.error(errorMsg, { agentId, rootApiUrl, requestBodyExists: !!requestBody, watExists: !!testChatWat, onMessageExists: !!onMessage, onErrorExists: !!onError });
      if (onError) {
        onError({ type: 'error', message: errorMsg, customError: true, isConfigurationError: true });
      }
      return { close: () => console.warn("Attempted to close a stream that was not properly initialized due to missing params.") };
    }

    let cleanRootApiUrl = rootApiUrl;
    if (typeof rootApiUrl === 'string' && rootApiUrl.endsWith('/')) {
        cleanRootApiUrl = rootApiUrl.slice(0, -1);
    }
    const sseUrl = `${cleanRootApiUrl}/api/v1/agents/${agentId}/conversations/stream`;

    const controller = new AbortController(); // To allow aborting the fetch request

    const connect = async () => {
      try {
        console.log("agentTestChatService (fetch): Attempting POST to URL:", sseUrl);
        console.log("agentTestChatService (fetch): With WAT:", testChatWat ? testChatWat.substring(0,20) + "..." : "NO WAT", "Body:", requestBody);

        const response = await fetch(sseUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${testChatWat}`,
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
          },
          body: JSON.stringify(requestBody),
          signal: controller.signal, // Link abort controller
        });

        console.log("agentTestChatService (fetch): Response status:", response.status);

        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json(); // Try to parse error from backend
          } catch (e) {
            errorData = { detail: response.statusText || `HTTP error ${response.status}` };
          }
          const err = new Error(errorData.detail || `Failed to connect to stream: ${response.status}`);
          // Attach status and full body to the error object so onError can use it
          err.status = response.status;
          err.responseBody = errorData;
          throw err;
        }

        // Call onOpen if provided, after successful connection
        if (onOpen) {
          onOpen({ type: 'open', message: 'Connection established' });
        }

        if (!response.body) {
          throw new Error("Response body is null, cannot read stream.");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();
          if (done) {
            console.log("agentTestChatService (fetch): Stream finished.");
            // If there's remaining data in buffer, try to process it
            if (buffer.trim().startsWith("data:")) {
                try {
                    const jsonPart = buffer.substring(buffer.indexOf("data:") + 5).trim();
                    if (jsonPart) {
                        onMessage({ data: jsonPart }); // Pass as string, AgentInteractTab will parse
                    }
                } catch (e) { console.error("Error processing residual buffer on stream end:", e); }
            }
            break; // Exit loop
          }

          buffer += decoder.decode(value, { stream: true });
          
          let eolIndex;
          // SSE messages are separated by double newlines ("\n\n")
          while ((eolIndex = buffer.indexOf('\n\n')) >= 0) {
            const messageBlock = buffer.substring(0, eolIndex).trim(); // Get data up to \n\n
            buffer = buffer.substring(eolIndex + 2); // Consume including the \n\n

            if (messageBlock.startsWith("data:")) {
              const jsonString = messageBlock.substring(5).trim(); // Get content after "data:"
              if (jsonString) {
                // Pass the raw JSON string, AgentInteractTab's onMessage will parse it
                onMessage({ data: jsonString });
              }
            } else if (messageBlock.startsWith(":")) {
              // This is a comment (e.g., heartbeat), ignore or handle if needed
              // console.log("SSE Heartbeat/Comment:", messageBlock);
            }
            // Handle other SSE fields like event: or id: if necessary in the future
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('agentTestChatService (fetch): Stream aborted by client.');
          // Call onError with a specific type or message for abortion
          onError({ type: 'abort', message: 'Stream aborted by client.' });
        } else {
          console.error('agentTestChatService (fetch): Error during stream connection or processing:', error);
          // Pass status and originalError (which might contain responseBody)
          onError({ type: 'error', message: error.message, status: error.status, originalError: error });
        }
      }
    };

    connect(); // Start the connection process

    // Return an object with a close method that aborts the fetch
    return {
      close: () => {
        console.log("agentTestChatService (fetch): Aborting stream connection...");
        controller.abort();
      }
    };
  },

  /**
   * Fetches the message history for a specific test chat conversation.
   * This assumes your `apiClient` (axios instance) is configured for the
   * service that hosts this endpoint (likely the same as init-test-chat).
   * The `testChatWat` should be used for authentication if this endpoint is protected.
   */
  async getConversationHistory(agentId, conversationId, testChatWat) {
    if (!agentId || !conversationId || !testChatWat) {
      const missingParams = [];
      if (!agentId) missingParams.push("Agent ID");
      if (!conversationId) missingParams.push("Conversation ID");
      if (!testChatWat) missingParams.push("Test Chat WAT");
      return { 
        isError: true, 
        message: `Missing required parameters to fetch history: ${missingParams.join(', ')}.`, 
        status: 400 
      };
    }
    try {
      // IMPORTANT: Adjust this URL to your actual backend endpoint for fetching test chat history.
      // This endpoint must be secured, e.g., by checking the testChatWat.
      // It's common to use the `apiClient` (axios) here if it's on the same domain/service
      // as the `/init-test-chat` endpoint.
      const response = await apiClient.get(
        `/agents/${agentId}/test-chat/conversations/${conversationId}/history`, 
        {
          headers: {
            // If your apiClient already handles auth or this endpoint is public for test WATs,
            // you might not need to set Authorization header explicitly.
            // However, if the WAT is specifically for this interaction, include it.
            'Authorization': `Bearer ${testChatWat}`
          }
        }
      );
      // Expect response.data to be like: 
      // { 
      //   messages: [ 
      //     { id: "msg1", type: "user", content: "Hi", timestamp: "ISO_STRING" }, 
      //     { id: "msg2", type: "assistant", content: "Hello!", model: "gpt-3.5", timestamp: "ISO_STRING" },
      //     // ... other message types
      //   ] 
      // }
      return response.data; 
    } catch (error) {
      return handleApiError(error, `fetch conversation history for conversation ${conversationId}`);
    }
  },
};